import { PayloadErrand, PayloadServiceGroup } from '../../payload/payload-types.zod'
import { getId, getObject } from '../../payload/payload.helpers'
import z from 'zod'

import { DocumentDb, documentDbToResponse, DocumentResponseSchema } from './documents'
import { NoteResponseSchema, noteToResponse } from './notes'
import type { PayloadNote } from './notes'

export const ErrandDbSchema = PayloadErrand

const ErrandStatusSchema = z.union([
  z.literal('started'),
  z.literal('submitted'),
  z.literal('approved'),
  z.literal('rejected'),
])

// Swedish labels for the errand statuses, for prompt text and anything else the backend writes
// for a human to read. The UI has its own translations — this is not a substitute for those.
export const ERRAND_STATUS_LABEL: Record<z.infer<typeof ErrandStatusSchema>, string> = {
  started: 'Påbörjad',
  submitted: 'Inskickad',
  approved: 'Godkänd',
  rejected: 'Avslagen',
}

// No `.default([])` on these arrays, deliberately. Zod applies a default even for an ABSENT key,
// so `PATCH { status }` used to parse into `{ documents: [], status }` — and since the service
// spreads the parsed body straight into the update, changing an errand's status wiped every
// document attached to it. An omitted field must stay omitted.
export const ErrandCreateRequestSchema = z.object({
  serviceGroups: PayloadErrand.shape.serviceGroups.optional(),
  documents: z.array(z.string()).optional(),
  title: PayloadErrand.shape.title.optional(),
  status: PayloadErrand.shape.status.optional(),
})

// `documents` is deliberately NOT updatable here. Attaching and detaching goes through the
// documents endpoints, which do it atomically per document; letting a client PATCH the whole
// array back is how the wipe above became possible in the first place.
export const ErrandUpdateRequestSchema = z.object({
  title: PayloadErrand.shape.title.optional(),
  status: PayloadErrand.shape.status.optional(),
})

export const ServiceGroupResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  showMunicipalityLinks: z.boolean(),
  active: z.boolean(),
  links: PayloadServiceGroup.shape.links,
  services: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      description: z.string(),
      active: z.boolean(),
    }),
  ),
  updatedAt: z.string(),
  createdAt: z.string(),
})

export const ErrandResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  generatedFields: z.object({ summary: z.string().optional() }).optional(),
  thread: z.union([
    z.string(),
    z.object({ id: z.string(), updatedAt: z.string() }),
  ]),
  documents: DocumentResponseSchema.array(),
  notes: NoteResponseSchema.array(),
  status: ErrandStatusSchema,
  // Oldest first. Deliberately absent from the update request — history is derived from status
  // changes server-side, never posted by a client.
  statusHistory: z
    .array(
      z.object({
        status: ErrandStatusSchema,
        changedAt: z.string(),
        changedBy: z.string().optional(),
      }),
    )
    .optional(),
  serviceGroups: z
    .array(
      z.object({
        serviceGroup: ServiceGroupResponseSchema,
        services: z
          .array(z.object({ serviceId: z.string(), id: z.string().optional() }))
          .optional(),
        id: z.string().optional(),
      }),
    )
    .optional(),
  updatedAt: z.string(),
  createdAt: z.string(),
})

export const ErrandListResponseSchema = ErrandResponseSchema.array()

export type ErrandDb = z.infer<typeof ErrandDbSchema>

export type ErrandCreateRequest = z.infer<typeof ErrandCreateRequestSchema>

export type ErrandUpdateRequest = z.infer<typeof ErrandUpdateRequestSchema>

export type ErrandResponse = z.infer<typeof ErrandResponseSchema>

export type ErrandListResponse = z.infer<typeof ErrandListResponseSchema>

// ─── Proactive reaction progress (realtime database) ──────────────────────────

/**
 * How the errand's realtime node advertises reactions in flight.
 *
 * One entry per dispatched reaction, keyed by the same sanitised task key that dedupes the Cloud
 * Task, valued with when it was queued. A map rather than a single flag because an errand can have
 * two unrelated reactions in flight at once — a note finishing while the user also changes the
 * status — and one boolean cleared by whichever settles first would hide the other.
 *
 * Written next to `reactionAt` under `individuals/<individualId>/errands/<errandId>`, which is the
 * only subtree the realtime database rules let an authenticated user read.
 */
export type ErrandReactionPending = Record<string, string>

/**
 * An entry older than this is treated as abandoned — the worker died before it could clean up.
 *
 * The same reasoning as REGEN_STUCK_MS, with a much shorter window because a reaction is one model
 * call rather than a full regeneration sweep: a spinner that outlives the work is worse than one
 * that gives up slightly early, since the message itself still arrives via `reactionAt`.
 */
export const REACTION_STUCK_MS = 5 * 60 * 1000

/**
 * True while at least one reaction is genuinely still being composed.
 *
 * Entries are dropped by the handler on every terminal path, so anything left here is either in
 * flight or abandoned; the age check is what separates the two. Unparseable values are ignored
 * rather than trusted, so a malformed write cannot pin the indicator on.
 */
export function isErrandReactionPending(
  pending: ErrandReactionPending | undefined,
  now: number = Date.now(),
): boolean {
  if (!pending) return false

  return Object.values(pending).some(startedAt => {
    const startedMs = Date.parse(startedAt)

    return Number.isFinite(startedMs) && now - startedMs < REACTION_STUCK_MS
  })
}

export const errandDbToResponse = (data: ErrandDb): ErrandResponse => {
  return ErrandResponseSchema.parse({
    id: data.id,
    title: data.title,
    generatedFields: data.generatedFields,
    thread:
      typeof data.thread === 'string'
        ? data.thread
        : { id: data.thread.id, updatedAt: data.thread.updatedAt },
    documents: (data.documents ?? [])
      .map(d => {
        const document = getObject<DocumentDb>(d)
        return document ? documentDbToResponse(document) : undefined
      })
      .filter((d): d is NonNullable<typeof d> => Boolean(d)),
    notes: (data.notes ?? [])
      .map(n => {
        const note = getObject<Omit<PayloadNote, 'embedding'>>(n)
        return note ? noteToResponse(note) : undefined
      })
      .filter((n): n is NonNullable<typeof n> => Boolean(n)),
    status: data.status,
    statusHistory: data.statusHistory
      ?.map(entry => ({
        status: entry.status,
        changedAt: entry.changedAt,
        changedBy: entry.changedBy ? getId(entry.changedBy) : undefined,
      }))
      .sort((a, b) => a.changedAt.localeCompare(b.changedAt)),
    serviceGroups: data.serviceGroups
      ?.map(sg => {
        const serviceGroup = getObject(sg.serviceGroup)
        if (!serviceGroup) return undefined
        return { serviceGroup, services: sg.services, id: sg.id }
      })
      .filter((sg): sg is NonNullable<typeof sg> => Boolean(sg)),
    updatedAt: data.updatedAt,
    createdAt: data.createdAt,
  } satisfies ErrandResponse)
}
